/**
 * DashboardBuilderPage — `/reporting/dashboards/builder`.
 *
 * Wired to the real persistence path on service-reporting-api:
 *   GET    /v1/reporting/dashboards/platform           — list tiles
 *   POST   /v1/reporting/dashboards/tiles              — create
 *   POST   /v1/reporting/dashboards/tiles/:id/delete   — delete
 *
 * Tiles are scoped to the platform-wide dashboard (operatorId = null) so the
 * page works without per-operator scoping UI.
 */
import { type JSX, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutDashboard, Plus, Trash2 } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  createDashboardTile,
  deleteDashboardTile,
  listDashboardTiles,
  type DashboardTileWire,
  type TileVizKind,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

function todayIso(): string { return new Date().toISOString().slice(0, 10); }
function thirtyDaysAgoIso(): string {
  const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
}

const VIZ_KINDS: readonly { value: TileVizKind; label: string }[] = [
  { value: 'SINGLE_VALUE', label: 'Single value' },
  { value: 'TIME_SERIES', label: 'Time series' },
  { value: 'TABLE', label: 'Table' },
  { value: 'HEATMAP', label: 'Heat map' },
];

export function DashboardBuilderPage(): JSX.Element {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [reportKey, setReportKey] = useState('DAILY_REVENUE_BY_REGION');
  const [vizKind, setVizKind] = useState<TileVizKind>('SINGLE_VALUE');
  const [formError, setFormError] = useState<string | null>(null);

  const tiles = useQuery<readonly DashboardTileWire[], Error>({
    queryKey: ['reporting', 'dashboard-tiles', 'platform'],
    queryFn: () => listDashboardTiles('platform'),
    retry: false,
  });

  const createM = useMutation({
    mutationFn: () =>
      createDashboardTile({
        operatorId: null,
        sequence: tiles.data?.length ?? 0,
        reportKey: reportKey.trim(),
        params: { fromDate: thirtyDaysAgoIso(), toDate: todayIso() },
        vizKind,
        title: title.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reporting', 'dashboard-tiles'] });
      setTitle('');
      setFormError(null);
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Create failed'),
  });

  const removeM = useMutation({
    mutationFn: (id: string) => deleteDashboardTile(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reporting', 'dashboard-tiles'] }),
  });

  function addTile(): void {
    if (title.trim().length === 0 || reportKey.trim().length === 0) {
      setFormError('Title and report key are required');
      return;
    }
    createM.mutate();
  }

  const denied =
    tiles.isError && tiles.error instanceof ApiError &&
    (tiles.error.status === 401 || tiles.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard builder"
        description="Compose a platform dashboard from report-key-backed tiles."
        actions={
          <Link
            to="/reporting/dashboards/sharing"
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light transition-colors"
          >
            Sharing →
          </Link>
        }
      />

      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/getDashboard</code> + <code className="font-mono">Reporting/createTile</code> required.
        </DeferredNotice>
      )}

      {!denied && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <LayoutDashboard className="w-4 h-4 text-nipost-green" />
            <h3 className="font-semibold text-gray-900">Add tile</h3>
          </div>
          {formError && (
            <p className="text-xs text-red-600 mb-3">{formError}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Tile title"
              aria-label="Tile title"
              data-testid="tile-title-input"
              className="px-3 py-2 text-sm border border-border rounded-lg"
            />
            <input
              type="text"
              value={reportKey}
              onChange={(e) => setReportKey(e.target.value)}
              placeholder="Report key (eg. DAILY_REVENUE_BY_REGION)"
              aria-label="Report key"
              data-testid="tile-report-key-input"
              className="px-3 py-2 text-sm font-mono border border-border rounded-lg md:col-span-2"
            />
            <select
              value={vizKind}
              onChange={(e) => setVizKind(e.target.value as TileVizKind)}
              aria-label="Visualisation kind"
              data-testid="tile-viz-select"
              className="px-3 py-2 text-sm border border-border rounded-lg"
            >
              {VIZ_KINDS.map((v) => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={addTile}
            disabled={createM.isPending}
            data-testid="tile-add-btn"
            className="mt-3 inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            {createM.isPending ? 'Adding…' : 'Add tile'}
          </button>
        </div>
      )}

      <div className="bg-white border border-border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3">
          Current tiles ({tiles.data?.length ?? 0})
        </h3>
        {tiles.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : tiles.isError && !denied ? (
          <p className="text-sm text-red-600">{tiles.error.message}</p>
        ) : (tiles.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-muted">No tiles yet — add one above.</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="tile-list">
            {(tiles.data ?? []).map((t) => (
              <li key={t.id} className="flex items-center justify-between py-3" data-testid="tile-row">
                <div>
                  <p className="text-sm font-medium text-gray-900">{t.title}</p>
                  <p className="text-xs text-muted">
                    <code className="font-mono">{t.reportKey}</code> · {t.vizKind} · seq={t.sequence}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeM.mutate(t.id)}
                  aria-label={`Remove ${t.title}`}
                  data-testid="tile-remove-btn"
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
